import Vue from "vue";
import eventBus from "./eventBus";

// Public rollout: offer install to every mobile visitor, including logged-out.
export const PWA_PUBLIC_ROLLOUT = true;

// Used only when PWA_PUBLIC_ROLLOUT is false. 2 = TBS.
export const PWA_TEST_COMPANY_IDS = [2];

const BANNER_DISMISSED_KEY = "ebm-pwa-banner-dismissed";
const PWA_ATTR = "data-ebm-pwa";

export const pwaUiState = Vue.observable({
  offered: false,
});

let deferredPrompt = null;
let listenersBound = false;
let serviceWorkerPromise = null;

export function ensureServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }
  if (!serviceWorkerPromise) {
    serviceWorkerPromise = navigator.serviceWorker.register("/sw.js").catch((err) => {
      serviceWorkerPromise = null;
      return Promise.reject(err);
    });
  }
  return serviceWorkerPromise;
}

export function isMobileViewport() {
  return window.matchMedia("(max-width: 640px)").matches;
}

export function isStandaloneDisplay() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIosDevice() {
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua);
  const iPadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

export function isPwaInstallAllowed(user) {
  if (PWA_PUBLIC_ROLLOUT) return true;
  if (!user || user.companyId == null) return false;
  const companyId = Number(user.companyId);
  return PWA_TEST_COMPANY_IDS.some((id) => Number(id) === companyId);
}

export function isPwaInstallOffered(user) {
  return isMobileViewport() && isPwaInstallAllowed(user) && !isStandaloneDisplay();
}

export function isPwaBannerDismissed() {
  return localStorage.getItem(BANNER_DISMISSED_KEY) === "1";
}

export function dismissPwaBanner() {
  localStorage.setItem(BANNER_DISMISSED_KEY, "1");
  eventBus.$emit("pwa-banner-dismissed");
}

export function syncPwaInstallState(user) {
  const offered = isPwaInstallOffered(user);
  pwaUiState.offered = offered;
  if (isStandaloneDisplay() || offered) {
    enablePwa();
    return;
  }
  disablePwaMeta();
}

export async function requestPwaInstall() {
  if (isStandaloneDisplay()) return "installed";

  if (isIosDevice()) {
    eventBus.$emit("pwa-show-ios-help");
    return "ios";
  }

  if (deferredPrompt) {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    eventBus.$emit("pwa-prompt-resolved", choice);
    return choice.outcome === "accepted" ? "accepted" : "dismissed";
  }

  eventBus.$emit("pwa-show-browser-help");
  return "fallback";
}

function enablePwa() {
  bindInstallListeners();
  attachPwaMeta();
  registerServiceWorker();
}

function bindInstallListeners() {
  if (listenersBound) return;
  listenersBound = true;
  window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  window.addEventListener("appinstalled", onAppInstalled);
}

function onBeforeInstallPrompt(event) {
  event.preventDefault();
  deferredPrompt = event;
  eventBus.$emit("pwa-prompt-available");
}

function onAppInstalled() {
  deferredPrompt = null;
  dismissPwaBanner();
  eventBus.$emit("pwa-app-installed");
}

function attachPwaMeta() {
  if (!document.querySelector(`link[rel="manifest"][${PWA_ATTR}]`)) {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/site.webmanifest";
    link.setAttribute(PWA_ATTR, "true");
    document.head.appendChild(link);
  }

  upsertMeta("theme-color", "#b91c1c");
  upsertMeta("apple-mobile-web-app-capable", "yes");
  upsertMeta("mobile-web-app-capable", "yes");
  upsertMeta("apple-mobile-web-app-title", "EBM");
  upsertMeta("apple-mobile-web-app-status-bar-style", "black-translucent");

  if (!document.querySelector(`link[rel="apple-touch-icon"][${PWA_ATTR}]`)) {
    const icon = document.createElement("link");
    icon.rel = "apple-touch-icon";
    icon.href = "/apple-touch-icon.png";
    icon.setAttribute(PWA_ATTR, "true");
    document.head.appendChild(icon);
  }
}

function disablePwaMeta() {
  document.querySelectorAll(`[${PWA_ATTR}]`).forEach((el) => el.remove());
}

function upsertMeta(name, content) {
  let meta = document.querySelector(`meta[name="${name}"][${PWA_ATTR}]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    meta.setAttribute(PWA_ATTR, "true");
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function registerServiceWorker() {
  ensureServiceWorker();
}
